import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import SEOHead from "@/components/SEOHead";
import { usePublishedCaseStudies } from "@/lib/cms";
import { buildBreadcrumb, buildWebPage } from "@/lib/seo/schemas";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const CaseStudies = () => {
  const { data: caseStudies = [] } = usePublishedCaseStudies();

  return (
    <div>
      <SEOHead
        title="Coaching & Training Case Studies Dubai | MVIBE Results"
        description="Real MVIBE client outcomes across executive coaching, EI, campus-to-corporate and career clarity engagements in Dubai, India and international teams."
        canonical="https://mvibeon.com/case-studies"
        schema={[
          buildWebPage({
            name: "MVIBE Case Studies",
            description: "Client outcomes from MVIBE coaching and corporate training engagements.",
            path: "/case-studies",
            type: "CollectionPage",
          }),
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Case Studies", path: "/case-studies" },
          ]),
        ]}
      />

      <section className="bg-primary py-14 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-primary-foreground">Case Studies</span>
          </nav>
          <motion.h1 {...fadeUp()} className="mx-auto max-w-4xl text-3xl font-heading font-bold text-primary-foreground md:text-5xl lg:text-6xl">
            Real Transformations. Real Results.
          </motion.h1>
          <motion.p {...fadeUp(0.1)} className="mx-auto mt-3 max-w-2xl text-lg font-sans text-primary-foreground/80">
            Explore how MVIBE helps leaders, students, and professionals move from friction to clarity through targeted interventions.
          </motion.p>
        </div>
      </section>

      <section className="bg-background py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-6 md:grid-cols-3">
            {caseStudies.map((cs, i) => (
              <motion.article
                key={cs.slug}
                {...fadeUp(i * 0.1)}
                whileHover={{ y: -4 }}
                className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition-shadow hover:shadow-lg"
              >
                <img src={cs.image} alt={cs.title} className="h-52 w-full object-cover" loading="lazy" />
                <div className="p-5">
                  <Badge className="mb-2.5 bg-accent text-accent-foreground">{cs.category}</Badge>
                  <h2 className="text-lg font-heading font-bold leading-snug text-foreground">{cs.title}</h2>
                  <p className="mt-3 text-base leading-7 text-muted-foreground">{cs.summary}</p>
                  <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <p><span className="font-semibold text-foreground">Client:</span> {cs.clientLabel}</p>
                    <p><span className="font-semibold text-foreground">Engagement:</span> {cs.engagementLabel}</p>
                  </div>
                  <Link
                    to={`/case-studies/${cs.slug}`}
                    className="mt-5 inline-flex items-center gap-2 text-base font-semibold text-primary hover:underline"
                  >
                    Read Full Case Study <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
